import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { api } from '../../lib/useEntity';
import { DescripcionProveedor, EtiquetaFotoProveedor } from './HeredadoProveedor';
import { fmt } from '../../lib/formatters';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { useFotosCatalogo } from './useFotosCatalogo';
import { Spinner } from '../ui/Spinner';
import { Select } from '../ui/Select';
import { toast } from '../../lib/toast';
import { RotacionSubtipo } from './RotacionSubtipo';
import { AccionComercialBadge, AccionComercialSelect } from './AccionComercial';
import {
  documentacionProducto,
  precioClienteDe,
  type CatalogoRow,
  type DocumentacionHechos,
} from '../../../shared/catalogo-productos';

/**
 * Vista catálogo del listado de productos: tarjetas «estilo web», 4 por fila,
 * con la foto principal, el estado y la acción comercial sobre la foto (los
 * mismos chips que la tabla y la ficha), precio al cliente, PVPr,
 * margen, stock, semanas de stock y pendiente con su ETA. «Vista rápida» abre un popup con la ficha resumida.
 *
 * Recibe las filas ya filtradas por el DataTable (DataTable.renderBody), así
 * que buscador, filtros en cascada y vistas guardadas valen igual que en la
 * tabla.
 */


const pendienteDe = (r: CatalogoRow) => Number(r.uds_pedidas_efectivas ?? r.uds_pedidas ?? 0);

function Dato({ label, children, title }: { label: string; children: React.ReactNode; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <div className="truncate text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="truncate text-sm font-semibold text-slate-800">{children}</div>
    </div>
  );
}

function Foto({ url, alt, className, proveedor = false }: { url: string | undefined; alt: string; className: string; proveedor?: boolean }) {
  if (!url) return <div className={`${className} flex items-center justify-center text-xs text-slate-300`}>Sin foto</div>;
  if (!proveedor) return <img src={url} alt={alt} loading="lazy" className={`${className} object-contain`} />;
  // Heredada del producto de proveedor: el producto no tiene foto propia.
  return (
    <div className="relative">
      <img src={url} alt={alt} loading="lazy" className={`${className} object-contain`} />
      <EtiquetaFotoProveedor className="absolute bottom-1 left-1" />
    </div>
  );
}

export function CatalogoProductos<T extends CatalogoRow>({
  rows,
  clienteCodigo,
  loading,
  onVistaRapida,
  margen,
  semanasStock,
}: {
  rows: T[];
  clienteCodigo?: string;
  loading?: boolean;
  onVistaRapida: (row: T) => void;
  margen: (coste: number | null, pvpr: number | null) => number | null;
  semanasStock: (row: T) => number;
}) {
  const fotos = useFotosCatalogo(clienteCodigo);

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (rows.length === 0) return <EmptyState title="Sin resultados" description="Prueba a ajustar los filtros." />;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {rows.map((r) => {
        const precio = precioClienteDe(r);
        const pend = pendienteDe(r);
        const semanas = semanasStock(r);
        return (
          <div key={r.id} className="flex flex-col rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm transition hover:shadow-md">
            <div className="truncate font-mono text-xs font-semibold text-slate-700" title="Ref. / Modelo">
              {r.sku}{r.part_number ? <span className="font-normal text-slate-400"> · {r.part_number}</span> : null}
            </div>
            <div className="mt-0.5 line-clamp-2 min-h-[2rem] text-xs leading-4 text-slate-600" title={r.nombre}>{r.nombre}</div>
            <button
              type="button"
              onClick={() => onVistaRapida(r)}
              className="relative mt-1.5 block w-full cursor-pointer overflow-hidden rounded-md border border-slate-100 bg-white"
              title="Vista rápida"
            >
              <Foto url={fotos.data?.urls[r.id]} proveedor={fotos.data?.proveedor.has(r.id)} alt={r.nombre} className="aspect-[16/9] w-full p-1.5" />
              <span className="absolute inset-x-1.5 top-1.5 flex flex-wrap justify-end gap-1">
                <span className="rounded-full bg-white/90 shadow-sm"><Badge color={r.estado_color} dot>{r.estado_nombre ?? r.estado}</Badge></span>
                <span className="rounded-full bg-white/90 shadow-sm"><AccionComercialBadge accion={r.accion_comercial} /></span>
              </span>
            </button>
            <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1.5">
              <Dato
                label={precio.vendido ? 'P. coste' : 'Coste ficha'}
                title={precio.vendido
                  ? `Último precio de venta al cliente${r.precio_cliente_fecha ? ` (${fmt.date(r.precio_cliente_fecha)})` : ''}`
                  : 'Sin ventas registradas al cliente: coste de la ficha'}
              >
                {fmt.eur(precio.valor)}
              </Dato>
              <Dato label="PVPr">{fmt.eur(r.pvpr)}</Dato>
              <Dato label="Margen" title="Margen del cliente sobre el PVPr sin IVA">{fmt.pct(margen(precio.valor, r.pvpr))}</Dato>
              <Dato label="Stock actual">{r.stock_actual != null ? fmt.int(r.stock_actual) : '—'}</Dato>
              <Dato label="Sem. stock" title="Semanas de stock">
                {Number(r.stock_actual ?? 0) <= 0 ? '—' : Number.isFinite(semanas) ? fmt.int(semanas) : '∞'}
              </Dato>
              <Dato label="Stock pend." title={r.eta_proxima ? `ETA próxima entrada: ${fmt.date(r.eta_proxima)}` : undefined}>
                {pend ? fmt.int(pend) : '—'}
                {pend && r.eta_proxima
                  ? <div className="text-[10px] font-normal text-slate-500">ETA {fmt.date(r.eta_proxima)}</div>
                  : null}
              </Dato>
            </div>
            <button
              type="button"
              onClick={() => onVistaRapida(r)}
              className="mt-2 cursor-pointer self-start text-xs font-medium text-accent-700 hover:underline"
            >
              Vista rápida →
            </button>
          </div>
        );
      })}
    </div>
  );
}

type VistaRapidaData = {
  ean: string | null;
  part_number: string | null;
  nombre: string;
  ficha_descripcion: string | null;
  /** Descripción del producto de proveedor, solo si no hay propia. */
  descripcion_proveedor: string | null;
  proveedor_nombre: string | null;
  ultimo_pedido_proveedor: { fecha: string; unidades: number; precio_compra_usd: number | null; estado: string; proveedor_nombre: string | null } | null;
  eta_proxima: string | null;
  documentacion: DocumentacionHechos;
  ventas_mensuales: { mes: string; unidades: number; provisional: boolean }[];
  ventas_semanales: { semana_inicio: string; unidades: number; stock: number | null }[];
};

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * Tablita de evolución de ventas del popup: periodos en columnas (del más
 * antiguo al más reciente). Meses si el cliente manda ventas mensuales
 * (TICNOVA); si no, semanas con el stock de cada una (GAME).
 */
function EvolucionVentas({ d, mensual }: { d: VistaRapidaData | undefined; mensual: boolean }) {
  const meses = d?.ventas_mensuales ?? [];
  const semanas = d?.ventas_semanales ?? [];
  const usarMeses = mensual ? meses.length > 0 || semanas.length === 0 : meses.length > 0 && semanas.length === 0;
  const cols = usarMeses
    ? meses.map((m) => ({
      key: m.mes,
      label: `${MESES_CORTOS[Number(m.mes.slice(5, 7)) - 1]} ${m.mes.slice(2, 4)}`,
      title: m.provisional ? 'Mes en curso: datos provisionales' : undefined,
      uds: m.unidades,
      stock: null as number | null,
      provisional: m.provisional,
    }))
    : semanas.map((s) => ({ key: s.semana_inicio, label: fmt.date(s.semana_inicio).slice(0, 5), title: `Semana del ${fmt.date(s.semana_inicio)}`, uds: s.unidades, stock: s.stock, provisional: false }));
  const conStock = cols.some((c) => c.stock != null);
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
        Evolución de ventas · {usarMeses ? 'últimos meses' : 'últimas semanas'}
      </div>
      {!d ? <span className="text-sm text-slate-400">…</span> : cols.length === 0 ? (
        <span className="text-sm text-slate-400">Sin ventas registradas.</span>
      ) : (
        <div className="mt-1 overflow-x-auto">
          <table className="text-xs tabular-nums">
            <thead>
              <tr className="text-slate-500">
                <th className="pr-3 text-left font-medium" />
                {cols.map((c) => (
                  <th key={c.key} title={c.title} className={`px-2 text-right font-medium ${c.provisional ? 'italic text-warn-700' : ''}`}>
                    {c.label}{c.provisional ? '*' : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="pr-3 text-slate-500">Uds.</td>
                {cols.map((c) => <td key={c.key} className="px-2 text-right font-semibold text-slate-800">{fmt.int(c.uds)}</td>)}
              </tr>
              {conStock && (
                <tr>
                  <td className="pr-3 text-slate-500">Stock</td>
                  {cols.map((c) => <td key={c.key} className="px-2 text-right text-slate-500">{c.stock != null ? fmt.int(c.stock) : '—'}</td>)}
                </tr>
              )}
            </tbody>
          </table>
          {cols.some((c) => c.provisional) && <p className="mt-0.5 text-[10px] text-slate-400">* mes en curso, aún sin cerrar.</p>}
        </div>
      )}
    </div>
  );
}

/** Check informativo (no clicable) del pie de la vista rápida. */
function Check({ ok, label, title }: { ok: boolean; label: string; title?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${ok ? 'text-emerald-700' : 'text-slate-400'}`} title={title}>
      <span
        aria-hidden
        className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] font-bold leading-none ${ok ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white'}`}
      >
        {ok ? '✓' : ''}
      </span>
      {label}
      <span className="sr-only">{ok ? ': sí' : ': no'}</span>
    </span>
  );
}

export function VistaRapidaProducto<T extends CatalogoRow>({
  row,
  clienteCodigo,
  ventasMensuales,
  margen,
  semanasStock,
  onClose,
  onAbrir,
}: {
  row: T | null;
  clienteCodigo?: string;
  /** Cliente con ventas mensuales (TICNOVA): cobertura en meses; si no, semanal (GAME). */
  ventasMensuales: boolean;
  margen: (coste: number | null, pvpr: number | null) => number | null;
  semanasStock: (row: T) => number;
  onClose: () => void;
  onAbrir: (row: T) => void;
}) {
  const fotos = useFotosCatalogo(clienteCodigo);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['producto-vista-rapida', row?.id],
    queryFn: () => api.get<VistaRapidaData>(`/productos/${row!.id}/vista-rapida`),
    enabled: !!row,
  });
  const estados = useQuery({
    queryKey: ['list', '/admin/producto-estados'],
    queryFn: () => api.get<{ data: { codigo: string; nombre: string; color: string }[] }>('/admin/producto-estados'),
    enabled: !!row,
  });
  // Estado cambiado desde el popup: la fila que llega del listado no se
  // actualiza hasta que este se recarga, así que el popup lo recuerda.
  const [estadoNuevo, setEstadoNuevo] = useState<{ id: string; codigo: string } | null>(null);
  const [accionNueva, setAccionNueva] = useState<{ id: string; accion: string } | null>(null);
  const cambiarEstado = useMutation({
    mutationFn: (args: { id: string; codigo: string }) => api.put(`/productos/${args.id}`, { estado: args.codigo }),
    onSuccess: (_r, args) => {
      setEstadoNuevo(args);
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
      qc.invalidateQueries({ queryKey: ['producto', args.id] });
      const nombre = estados.data?.data.find((e) => e.codigo === args.codigo)?.nombre ?? args.codigo;
      toast.success('Estado cambiado', `${row?.sku ?? ''} → ${nombre}`);
    },
    onError: (err) => toast.error('No se pudo cambiar el estado', (err as Error).message),
  });
  if (!row) return null;
  const estadoCodigo = estadoNuevo?.id === row.id ? estadoNuevo.codigo : row.estado;
  const estadoInfo = estados.data?.data.find((e) => e.codigo === estadoCodigo);
  const d = q.data;
  const precio = precioClienteDe(row);
  const m = margen(precio.valor, row.pvpr);
  const pend = pendienteDe(row);
  const semanas = semanasStock(row);
  const up = d?.ultimo_pedido_proveedor ?? null;
  const desc = d?.ficha_descripcion ?? null;
  const docs = d ? documentacionProducto(d.documentacion) : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={<span className="font-mono">{row.sku}</span>}
      footer={
        <>
          <div className="mr-auto flex flex-wrap items-center gap-x-4 gap-y-1">
            {docs ? (
              <>
                <Check ok={docs.descripcion} label="Descripción" />
                <Check
                  ok={docs.ficha.completa}
                  label="Ficha completa"
                  title={docs.ficha.completa ? 'Datos básicos completos y ficha revisada' : `Falta: ${docs.ficha.faltan.join(', ')}`}
                />
                <Check ok={docs.manual} label="Manual de usuario" />
                <Check ok={docs.packaging} label="Packaging" />
              </>
            ) : <span className="text-xs text-slate-400">Comprobando documentación…</span>}
          </div>
          <Button onClick={() => onAbrir(row)}>Abrir ficha completa</Button>
        </>
      }
    >
      <div className="grid gap-5 md:grid-cols-[16rem_minmax(0,1fr)]">
        <div className="self-start">
          <div className="relative overflow-hidden rounded-md border border-slate-200 bg-white">
            <Foto url={fotos.data?.urls[row.id]} proveedor={fotos.data?.proveedor.has(row.id)} alt={row.nombre} className="aspect-square w-full p-2" />
            <span className="absolute inset-x-1.5 top-1.5 flex flex-wrap justify-end gap-1">
              <span className="rounded-full bg-white/90 shadow-sm"><Badge color={estadoInfo?.color ?? row.estado_color} dot>{estadoInfo?.nombre ?? row.estado_nombre ?? row.estado}</Badge></span>
              <span className="rounded-full bg-white/90 shadow-sm"><AccionComercialBadge accion={accionNueva?.id === row.id ? accionNueva.accion : row.accion_comercial} /></span>
            </span>
          </div>
          <RotacionSubtipo productoId={row.id} />
        </div>
        <div className="min-w-0 space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Dato label="EAN"><span className="font-mono">{d?.ean ?? row.ean ?? '—'}</span></Dato>
            <Dato label="Modelo"><span className="font-mono">{d?.part_number ?? row.part_number ?? '—'}</span></Dato>
            <div className="col-span-2 min-w-0 sm:col-span-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Estado</div>
              <Select
                aria-label="Estado del producto"
                options={(estados.data?.data ?? []).map((e) => ({ value: e.codigo, label: e.nombre }))}
                value={estadoCodigo}
                emptyLabel={null}
                disabled={cambiarEstado.isPending || !estados.data}
                onChange={(e) => cambiarEstado.mutate({ id: row.id, codigo: e.target.value })}
                className="mt-0.5 w-full py-1 text-sm"
              />
            </div>
            <div className="col-span-2 min-w-0 sm:col-span-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Acción comercial</div>
              <AccionComercialSelect
                key={row.id}
                productoId={row.id}
                value={accionNueva?.id === row.id ? accionNueva.accion : (row.accion_comercial ?? 'MANTENER')}
                onChanged={(accion) => setAccionNueva({ id: row.id, accion })}
                className="mt-0.5 w-full py-1 text-sm"
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Nombre completo</div>
            <div className="text-sm font-semibold text-slate-800">{d?.nombre ?? row.nombre}</div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Descripción comercial</div>
            {q.isLoading
              ? <Spinner />
              : desc
                ? (/<\/?[a-z][^>]*>/i.test(desc)
                  ? <div className="prose prose-sm max-h-40 max-w-none overflow-auto text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(desc) }} />
                  : <p className="max-h-40 overflow-auto whitespace-pre-line text-sm text-slate-700">{desc}</p>)
                : d?.descripcion_proveedor
                  ? <DescripcionProveedor texto={d.descripcion_proveedor} className="max-h-40 overflow-auto" />
                  : <span className="text-sm text-slate-400">—</span>}
          </div>

          <div className="grid grid-cols-3 gap-3 border-t border-slate-100 pt-3">
            <Dato
              label={precio.vendido ? 'P. coste cliente' : 'Coste (ficha)'}
              title={precio.vendido && row.precio_cliente_fecha ? `Último precio de venta al cliente (${fmt.date(row.precio_cliente_fecha)})` : undefined}
            >
              {fmt.eur(precio.valor)}
            </Dato>
            <Dato label="PVPr">{fmt.eur(row.pvpr)}</Dato>
            <Dato label="Margen" title="Margen del cliente sobre el PVPr sin IVA">{fmt.pct(m)}</Dato>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Dato label="Stock actual">{row.stock_actual != null ? fmt.int(row.stock_actual) : '—'}</Dato>
            <Dato label="Stock pendiente">{pend ? fmt.int(pend) : '—'}</Dato>
            <Dato label="ETA próximo pedido">{q.isLoading ? '…' : fmt.date(d?.eta_proxima ?? null)}</Dato>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {ventasMensuales ? (
              <>
                <Dato label="Venta media 3 meses">{row.ventas_media_3m != null ? fmt.int(Math.round(Number(row.ventas_media_3m))) : '—'}</Dato>
                <Dato label="Meses stock">
                  {Number(row.stock_actual ?? 0) <= 0 ? '—' : row.meses_stock == null ? '∞' : Number(row.meses_stock).toFixed(1)}
                </Dato>
              </>
            ) : (
              <>
                <Dato label="Ventas últ. 4 semanas">{fmt.int(row.ventas_l4wk ?? 0)}</Dato>
                <Dato label="Semanas stock">
                  {Number(row.stock_actual ?? 0) <= 0 ? '—' : Number.isFinite(semanas) ? fmt.int(semanas) : '∞'}
                </Dato>
              </>
            )}
          </div>
          <EvolucionVentas d={d} mensual={ventasMensuales} />
          <div className="grid grid-cols-3 gap-3 border-t border-slate-100 pt-3">
            <Dato label="Proveedor">{d?.proveedor_nombre ?? up?.proveedor_nombre ?? row.proveedor_nombre ?? '—'}</Dato>
            <Dato label="Último coste proveedor" title={up ? `Pedido del ${fmt.date(up.fecha)}` : undefined}>
              {q.isLoading ? '…' : fmt.usd(up?.precio_compra_usd ?? null)}
            </Dato>
            <Dato label="Último pedido (uds)" title={up ? `Pedido del ${fmt.date(up.fecha)}` : undefined}>
              {q.isLoading ? '…' : up ? `${fmt.int(up.unidades)} · ${fmt.date(up.fecha)}` : '—'}
            </Dato>
          </div>
        </div>
      </div>
    </Modal>
  );
}
